import React from "react";
import { useTranslation } from "react-i18next";
import { YesNoRadioButtons } from "../../../common/radio-buttons/radio-buttons";
import FullPicker from "../../../common/picker/full-picker";
import { ModeFlags } from "../../../../hooks/game-settings/useGameSettings";

interface AdvancedSettings {
  numSwitches: number;
  selfShuffle: boolean;
  audiences: boolean;
  extraReadingTime: boolean;
  hideAuthors: boolean;
  allowDuplicateCharacters: boolean;
  autoSyncPartsToPlayers: boolean;
}

interface AdvancedSettingsProps {
  settings: AdvancedSettings;
  onChange: (settings: Partial<AdvancedSettings>) => void;
  modeFlags: ModeFlags;
}

const AdvancedSettings: React.FC<AdvancedSettingsProps> = ({
  settings,
  onChange,
  modeFlags,
}) => {
  const { t } = useTranslation(["game-settings", "common"]);

  const isSolo = !!modeFlags.solo_writing;
  const isOtp = !!modeFlags.otp_mode;
  const isAutoSyncFeatureEnabled = import.meta.env.VITE_FEATURE_AUTO_SYNC_PARTS_TO_PLAYERS === 'true';

  return (
    <>
      <FullPicker
        label={t("body.numParts")}
        subLabel={t("body.numParts2")}
        value={settings.numSwitches}
        onSelect={(value) => onChange({ numSwitches: value })}
        disabled={isAutoSyncFeatureEnabled && settings.autoSyncPartsToPlayers}
        options={[
          { value: 3 },
          { value: 4 },
          { value: 5 },
          { value: 6 },
          { value: 7 },
          { value: 8 },
          { value: 9 },
          { value: 10 },
          { value: 3, isCustom: true },
        ]}
      />

      {isAutoSyncFeatureEnabled && (
        <YesNoRadioButtons
          label={t("body.autoSyncPartsToPlayers")}
          subLabel={t("body.autoSyncPartsToPlayers2")}
          value={settings.autoSyncPartsToPlayers}
          onSelect={(value) => onChange({ autoSyncPartsToPlayers: value })}
        />
      )}

      {!isSolo && !isOtp && (
        <YesNoRadioButtons
          label={t("body.selfShuffle")}
          subLabel={t("body.selfShuffle2")}
          value={settings.selfShuffle}
          onSelect={(value) => onChange({ selfShuffle: value })}
        />
      )}

      {isSolo && (
        <YesNoRadioButtons
          label={t("body.autoShuffle")}
          subLabel={t("body.autoShuffle2")}
          value={settings.selfShuffle}
          onSelect={(value) => onChange({ selfShuffle: value })}
        />
      )}

      {!isSolo && (
        <YesNoRadioButtons
          label={t("body.audience")}
          value={settings.audiences}
          onSelect={(value) => onChange({ audiences: value })}
        />
      )}

      {!isSolo && (
        <YesNoRadioButtons
          label={t("body.extraReadingTime")}
          value={settings.extraReadingTime}
          onSelect={(value) => onChange({ extraReadingTime: value })}
        />
      )}

      {!isSolo && (
        <YesNoRadioButtons
          label={t("body.hideAuthors")}
          value={settings.hideAuthors}
          onSelect={(value) => onChange({ hideAuthors: value })}
        />
      )}

      <YesNoRadioButtons
        label={t("body.allowDuplicateCharacters")}
        subLabel={t("body.allowDuplicateCharacters2")}
        value={settings.allowDuplicateCharacters}
        onSelect={(value) => onChange({ allowDuplicateCharacters: value })}
      />
    </>
  );
};

export default AdvancedSettings;
export type { AdvancedSettings, };
